import React from "react";
import { Check, Eye, History, Image as ImageIcon, RotateCcw, Video as VideoIcon } from "lucide-react";

const DATE_FORMATTER = new Intl.DateTimeFormat("zh-CN", {
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const FULL_DATE_FORMATTER = new Intl.DateTimeFormat("zh-CN", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const getEntryUrl = (entry) => entry?.url
  || entry?.resultUrl
  || entry?.result_url
  || entry?.imageUrl
  || entry?.videoUrl
  || entry?.outputUrl
  || entry?.thumbnail
  || "";

const getEntryTime = (entry) => entry?.createdAt
  || entry?.created_at
  || entry?.generatedAt
  || entry?.lastGeneratedAt
  || entry?.finishedAt
  || entry?.timestamp
  || null;

const getEntryKey = (entry, index) => {
  const identity = entry?.id
    || entry?.historyId
    || entry?.taskId
    || entry?.resourceId
    || getEntryUrl(entry)
    || "generation";
  return `${identity}-${getEntryTime(entry) || index}`;
};

const isVideoEntry = (entry, mediaType) => {
  const type = String(entry?.mediaType || entry?.type || mediaType || "image").toLowerCase();
  return type.includes("video");
};

const formatHistoryTime = (value) => {
  if (!value) return { compact: "时间未知", full: "时间未知" };

  const date = new Date(value);
  const timestamp = date.getTime();
  if (!Number.isFinite(timestamp)) return { compact: "时间未知", full: "时间未知" };

  const elapsed = Math.max(0, Date.now() - timestamp);
  const minutes = Math.floor(elapsed / 60_000);
  const hours = Math.floor(elapsed / 3_600_000);
  const days = Math.floor(elapsed / 86_400_000);
  const relative = minutes < 1
    ? "刚刚"
    : minutes < 60
      ? `${minutes}分钟前`
      : hours < 24
        ? `${hours}小时前`
        : days < 7
          ? `${days}天前`
          : null;
  const local = DATE_FORMATTER.format(date);

  return {
    compact: relative ? `${relative} · ${local.slice(-5)}` : local,
    full: FULL_DATE_FORMATTER.format(date),
  };
};

const stopInteraction = (event) => event.stopPropagation();

/**
 * 生成节点内的横向版本历史。
 * 回调均以完整历史项为第一参数；onPreview 的第二参数为已解析的媒体 URL。
 */
const GenerationHistoryStrip = ({
  history = [],
  currentUrl = "",
  currentId = "",
  mediaType = "image",
  disabled = false,
  showHeader = true,
  onPreview,
  onSelect,
  onRestoreParams,
}) => {
  const entries = Array.isArray(history) ? history : [];
  const hasCurrentId = currentId !== undefined && currentId !== null && currentId !== "";
  const currentIndexById = hasCurrentId
    ? entries.findIndex((entry) => String(entry?.id) === String(currentId))
    : -1;
  const explicitCurrentIndex = entries.findIndex((entry) => entry?.isCurrent === true);
  const currentIndexByUrl = currentUrl
    ? entries.findIndex((entry) => getEntryUrl(entry) === currentUrl)
    : -1;
  // 同一 URL 可能由多个任务返回；当前版本始终只高亮一个，优先使用稳定历史 id。
  const currentIndex = currentIndexById >= 0
    ? currentIndexById
    : explicitCurrentIndex >= 0
      ? explicitCurrentIndex
      : currentIndexByUrl;

  return (
    <section
      className="nodrag nopan nowheel min-w-0"
      aria-label="生成历史"
      onPointerDown={stopInteraction}
      onClick={stopInteraction}
      onDoubleClick={stopInteraction}
      onContextMenu={stopInteraction}
    >
      {showHeader && (
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <History size={13} aria-hidden="true" style={{ color: "var(--color-text-muted)" }} />
            <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>
              生成历史
            </span>
          </div>
          {entries.length > 0 && (
            <span
              className="shrink-0 rounded-full px-1.5 py-0.5 text-2xs font-mono-num"
              style={{
                color: "var(--color-text-muted)",
                background: "var(--color-bg-tertiary)",
                border: "1px solid var(--color-border-secondary)",
              }}
              aria-label={`共 ${entries.length} 个历史版本`}
            >
              {entries.length}
            </span>
          )}
        </div>
      )}

      {entries.length === 0 ? (
        <div
          className="flex min-h-16 items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-4 text-center"
          style={{
            color: "var(--color-text-muted)",
            background: "var(--color-bg-primary)",
            borderColor: "var(--color-border-secondary)",
          }}
        >
          {String(mediaType).toLowerCase().includes("video") ? (
            <VideoIcon size={14} aria-hidden="true" />
          ) : (
            <ImageIcon size={14} aria-hidden="true" />
          )}
          <span className="text-2xs">完成一次生成后，历史版本会显示在这里</span>
        </div>
      ) : (
        <div
          className="nowheel flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2"
          role="list"
          aria-label="历史版本列表"
        >
          {entries.map((entry, index) => {
            const url = getEntryUrl(entry);
            const video = isVideoEntry(entry, mediaType);
            const current = index === currentIndex;
            const time = formatHistoryTime(getEntryTime(entry));
            const mediaLabel = video ? "视频" : "图片";

            return (
              <article
                key={getEntryKey(entry, index)}
                role="listitem"
                className="nodrag nopan w-36 shrink-0 snap-start overflow-hidden rounded-lg transition-colors motion-reduce:transition-none"
                style={{
                  background: "var(--color-bg-primary)",
                  border: current
                    ? "1px solid var(--color-accent-primary)"
                    : "1px solid var(--color-border-secondary)",
                  boxShadow: current ? "0 0 0 2px var(--color-accent-light)" : "none",
                }}
                aria-current={current ? "true" : undefined}
              >
                <button
                  type="button"
                  className="group/preview nodrag nopan relative block h-20 w-full overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-primary)] disabled:cursor-default"
                  style={{ background: "var(--color-bg-tertiary)" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (url) onPreview?.(entry, url);
                  }}
                  disabled={!url || typeof onPreview !== "function"}
                  aria-label={url ? `预览第 ${index + 1} 个生成${mediaLabel}` : `第 ${index + 1} 个生成结果不可预览`}
                  title={url ? "点击预览" : "该版本没有可用地址"}
                >
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ color: "var(--color-text-muted)" }}
                    aria-hidden="true"
                  >
                    {video ? <VideoIcon size={20} /> : <ImageIcon size={20} />}
                  </div>
                  {url && (video ? (
                    <video
                      src={url}
                      className="relative h-full w-full object-cover"
                      muted
                      playsInline
                      preload="none"
                      aria-label={`第 ${index + 1} 个历史视频缩略图`}
                    />
                  ) : (
                    <img
                      src={url}
                      alt={`第 ${index + 1} 个历史图片缩略图`}
                      className="relative h-full w-full object-cover"
                      loading="lazy"
                      draggable="false"
                    />
                  ))}
                  {url && typeof onPreview === "function" && (
                    <span
                      className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition-opacity group-hover/preview:opacity-100 group-focus-visible/preview:opacity-100 motion-reduce:transition-none"
                      aria-hidden="true"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white">
                        <Eye size={14} />
                      </span>
                    </span>
                  )}
                  {current && (
                    <span
                      className="absolute left-1.5 top-1.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-2xs font-medium"
                      style={{
                        color: "var(--color-accent-contrast)",
                        background: "var(--color-accent-primary)",
                      }}
                    >
                      <Check size={9} strokeWidth={2.5} aria-hidden="true" />
                      当前版本
                    </span>
                  )}
                </button>

                <div className="space-y-1.5 p-2">
                  <time
                    className="block truncate text-2xs font-mono-num"
                    style={{ color: "var(--color-text-muted)" }}
                    dateTime={getEntryTime(entry) || undefined}
                    title={time.full}
                  >
                    {time.compact}
                  </time>
                  <div className="grid grid-cols-2 gap-1">
                    <button
                      type="button"
                      className="nodrag nopan inline-flex h-6 items-center justify-center gap-1 rounded px-1 text-2xs font-medium transition-colors hover:bg-[var(--color-bg-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-primary)] disabled:cursor-default disabled:opacity-55 motion-reduce:transition-none"
                      style={{
                        color: current ? "var(--color-success)" : "var(--color-text-secondary)",
                        border: "1px solid var(--color-border-secondary)",
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!disabled && !current) onSelect?.(entry);
                      }}
                      disabled={disabled || current || !url || typeof onSelect !== "function"}
                      aria-label={current ? "当前版本" : `将第 ${index + 1} 个历史版本设为当前`}
                      title={disabled ? "生成中暂不可切换版本" : current ? "已经是当前版本" : "设为当前版本"}
                    >
                      {current && <Check size={10} aria-hidden="true" />}
                      {current ? "当前" : "设为当前"}
                    </button>
                    <button
                      type="button"
                      className="nodrag nopan inline-flex h-6 items-center justify-center gap-1 rounded px-1 text-2xs font-medium transition-colors hover:bg-[var(--color-bg-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-primary)] disabled:cursor-default disabled:opacity-55 motion-reduce:transition-none"
                      style={{
                        color: "var(--color-text-secondary)",
                        border: "1px solid var(--color-border-secondary)",
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!disabled) onRestoreParams?.(entry);
                      }}
                      disabled={disabled || typeof onRestoreParams !== "function"}
                      aria-label={`恢复第 ${index + 1} 个历史版本的生成参数`}
                      title={disabled ? "生成中暂不可恢复参数" : "恢复本次生成参数"}
                    >
                      <RotateCcw size={10} aria-hidden="true" />
                      恢复参数
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default GenerationHistoryStrip;
